// Copie rapide d'une valeur affichée — composant partagé (tableau + modale).
//
// `Copyable` enveloppe une valeur déjà rendue : le bouton apparaît au survol et
// copie la **valeur affichée** (dates et montants sont donc copiés tels qu'on
// les lit à l'écran, pas au format brut de la base). Rien n'est rendu en plus
// si la valeur est vide — pas de bouton sur une cellule « — ».
//
// Aucun décalage de mise en page : le bouton occupe toujours sa place dans le
// flux (`.copyable` est un inline-flex), seule son opacité change au survol.
// Une superposition en `position:absolute` aurait recouvert le texte dans les
// cellules étroites du tableau Takata.
//
// Lecture seule : aucune donnée n'est modifiée, le composant est donc
// indépendant des droits d'édition (`editMode`) — il suffit d'avoir accès à la
// page pour copier ce qu'on y voit déjà.
import React, { useState, useRef, useEffect } from 'react';
import { copierTexte } from '../utils/clipboard';
import './Copyable.css';

const IconeCopier = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const IconeOk = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

export function CopyButton({ value, titre = 'Copier' }) {
  const [etat, setEtat] = useState(null); // null | 'ok' | 'ko'
  const timerRef = useRef(null);

  // Le timer doit mourir avec le composant : une ligne de tableau démontée
  // pendant le délai (changement de filtre, pagination) déclencherait sinon un
  // setState sur un composant absent.
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const onClick = async (e) => {
    // Les lignes du tableau et les cartes ouvrent une modale au clic : la copie
    // ne doit pas la déclencher au passage.
    e.stopPropagation();
    e.preventDefault();
    const ok = await copierTexte(value);
    setEtat(ok ? 'ok' : 'ko');
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setEtat(null), 1500);
  };

  return (
    <button
      type="button"
      className={`copy-btn${etat ? ` copy-btn--${etat}` : ''}`}
      onClick={onClick}
      onMouseDown={e => e.stopPropagation()}
      title={etat === 'ok' ? 'Copié' : etat === 'ko' ? 'Copie impossible' : titre}
      aria-label={titre}
    >
      {etat === 'ok' ? <IconeOk /> : <IconeCopier />}
    </button>
  );
}

// `value` : ce qui est copié. Par défaut, le texte des enfants s'ils sont une
// chaîne ou un nombre — utile pour `<Copyable>{d.vin}</Copyable>`.
export default function Copyable({ value, children, titre, className = '' }) {
  const brut = value !== undefined ? value : children;
  // Garde-fou : un `value` qui est un élément React (badge, icône…) n'a pas de
  // représentation textuelle — le copier donnerait « [object Object] ». Ces
  // valeurs sont rendues telles quelles, sans bouton.
  const copiable = typeof brut === 'string' || typeof brut === 'number';
  const texte = copiable ? String(brut).trim() : '';
  if (!texte) return <>{children}</>;
  return (
    <span className={`copyable ${className}`.trim()}>
      <span className="copyable-val">{children}</span>
      <CopyButton value={texte} titre={titre} />
    </span>
  );
}
